import React, { useState } from "react";
import {
  AlertOctagon, AlertTriangle, Calendar, CheckCircle2, FileText, Info, Loader2,
  Mail, MessageCircle, Phone, RefreshCw, Send, Share2, UserPlus,
} from "lucide-react";
import { api } from "../lib/api";
import useCustomerData from "../hooks/useCustomerData";
import { ComplianceEditor } from "../components/ParamEditors";
import { getCurrentCustomer } from "../lib/customerSession";
import { STATUS, Section, Modal, KpiCard, InsightsGrid, ChatPanel, ModuleHeader } from "../components/intel/IntelKit";
import { BACKEND_URL } from "../lib/backendUrl";

// ---------------------------------------------------------------------------
// SECTION 1 — KPIs
// ---------------------------------------------------------------------------
const KPIS = [
  { id: "linked",   label: "CA Linked",         value: "1",      sub: "Pankaj Joshi & Associates", status: "blue",  icon: UserPlus },
  { id: "shared",   label: "Reports Shared",    value: "18",     sub: "Last 12 months",            status: "green", icon: Share2 },
  { id: "login",    label: "Last CA Login",     value: "Apr 22", sub: "2 days ago",                status: "green", icon: Calendar },
  { id: "pending",  label: "Pending CA Actions",value: "3",      sub: "1 urgent · 1 review",       status: "amber", icon: AlertTriangle },
];

// ---------------------------------------------------------------------------
// SECTION 2 — Shared document queue
// ---------------------------------------------------------------------------
const STATUS_PILL = {
  viewed:        { label: "Viewed",        chip: "bg-emerald-100 text-emerald-700" },
  pending:       { label: "Pending",       chip: "bg-amber-100 text-amber-700"     },
  action_needed: { label: "Action needed", chip: "bg-red-100 text-red-700"         },
  not_shared:    { label: "Not shared",    chip: "bg-slate-200 text-slate-600"     },
  shared:        { label: "Shared",        chip: "bg-blue-100 text-blue-700"       },
};

const ACTION_STYLE = {
  "Re-send":  "border border-slate-300 bg-white text-[#1E3A5F] hover:bg-slate-50",
  "Share":    "bg-[#1E3A5F] text-white hover:bg-[#0F1B2D]",
  "Resolve":  "bg-red-600 text-white hover:bg-red-700",
  "Generate": "bg-emerald-600 text-white hover:bg-emerald-700",
};

const SEED_DOCS = [
  { id: "board_pack",  name: "Board Pack",                  status: "viewed",        action: "Re-send",  date: "—" },
  { id: "gstr3b",      name: "GSTR-3B Summary Apr",        status: "pending",       action: "Share",    date: "Apr 22" },
  { id: "itc_recon",   name: "ITC Reconciliation — Apr",   status: "action_needed", action: "Resolve",  date: "Apr 19" },
  { id: "advance_tax", name: "Advance Tax FY27 Estimation",status: "not_shared",    action: "Generate", date: "—" },
  { id: "p_and_l",     name: "P&L Statement — Mar",        status: "viewed",        action: "Re-send",  date: "Apr 5"  },
  { id: "stock_audit", name: "Stock Audit Mar Quarter",    status: "shared",        action: "Re-send",  date: "Apr 8"  },
];

function DocumentQueue() {
  const [docs, setDocs] = useState(SEED_DOCS);
  const [toast, setToast] = useState(null);

  const onAction = (id, action) => {
    setDocs((arr) => arr.map((d) =>
      d.id === id
        ? {
            ...d,
            status: action === "Resolve" ? "viewed" : action === "Generate" || action === "Share" ? "shared" : "shared",
            action: "Re-send",
            date: "Today",
          }
        : d
    ));
    setToast(`${action} → CA notified via WhatsApp & email.`);
    setTimeout(() => setToast(null), 2400);
  };

  return (
    <Section
      testid="ca-doc-queue"
      title="Shared document queue"
      subtitle="CA has read-only + comment access · Auto-PDF emails"
      right={<span className="hidden md:inline text-[11px] text-slate-500">Email invite · WhatsApp ping</span>}
    >
      <ul className="space-y-2">
        {docs.map((d) => {
          const pill = STATUS_PILL[d.status] || STATUS_PILL.viewed;
          return (
            <li key={d.id} data-testid={`ca-doc-${d.id}`}
              className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-2">
                <FileText size={14} className="mt-0.5 shrink-0 text-slate-500" />
                <div>
                  <div className="text-[12.5px] font-semibold text-[#0F1B2D]">{d.name}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">Last updated {d.date}</div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 md:self-center">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${pill.chip}`}>{pill.label}</span>
                <button data-testid={`ca-doc-action-${d.id}`} onClick={() => onAction(d.id, d.action)}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] font-semibold ${ACTION_STYLE[d.action] || ACTION_STYLE["Re-send"]}`}>
                  {d.action} →
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {toast && (
        <div data-testid="ca-doc-toast" className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
          {toast}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — CA Workflow Queue
// ---------------------------------------------------------------------------
const QUEUE = [
  { id: "itc_urgent", sev: "red",   label: "URGENT", icon: AlertOctagon, title: "ITC mismatch ₹48,200 · CA review needed by Apr 21",  ctaLabel: "Notify CA", ctaText: "WhatsApp message will be sent to CA Pankaj Joshi at +91 98XXXXX901 with the ITC mismatch summary." },
  { id: "kyc_review", sev: "amber", label: "REVIEW", icon: AlertTriangle,title: "Directors KYC due May 31",                            ctaLabel: "Schedule call", ctaText: "Open a 30-min slot in CA's calendar this week to discuss the KYC filing pack." },
  { id: "adv_tax",    sev: "blue",  label: "INFO",   icon: Info,        title: "Advance Tax Q1 FY27 estimation ready",                ctaLabel: "Share with CA", ctaText: "Generates the FY27 Q1 advance-tax PDF and shares it with CA via WhatsApp + email." },
];

function WorkflowQueue() {
  const [done, setDone] = useState({});
  const [modal, setModal] = useState(null);

  const confirm = () => {
    setDone((d) => ({ ...d, [modal.id]: modal.ctaLabel === "Schedule call" ? "Call scheduled" : modal.ctaLabel === "Notify CA" ? "WhatsApp sent" : "Shared with CA" }));
    setModal(null);
  };

  return (
    <Section testid="ca-workflow" title="CA Workflow Queue" subtitle="What your CA needs from you next">
      <ul className="space-y-2">
        {QUEUE.map((q) => {
          const s = STATUS[q.sev];
          const Icon = q.icon;
          return (
            <li key={q.id} data-testid={`ca-queue-${q.id}`}
              className={`flex items-start justify-between gap-3 rounded-lg ${s.left} ${s.soft} p-3`}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold ${s.text}`}>
                  <Icon size={11} /> {q.label}
                </span>
                <div className="text-[12.5px] font-semibold text-[#0F1B2D]">{q.title}</div>
              </div>
              {done[q.id] ? (
                <span data-testid={`ca-queue-done-${q.id}`} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                  <CheckCircle2 size={12} /> {done[q.id]}
                </span>
              ) : (
                <button data-testid={`ca-queue-cta-${q.id}`} onClick={() => setModal(q)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md bg-[#1E3A5F] px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-[#0F1B2D]">
                  {q.ctaLabel} →
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal ? modal.ctaLabel : "Confirm"} testid="ca-queue-modal">
        {modal && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-[#0F1B2D] font-semibold">{modal.title}</div>
            <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50/60 p-3 text-[12px] text-slate-700">{modal.ctaText}</div>
            <div className="flex items-center gap-2">
              <button data-testid="ca-queue-modal-confirm" onClick={confirm}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700">
                Confirm — {modal.ctaLabel}
              </button>
              <button data-testid="ca-queue-modal-cancel" onClick={() => setModal(null)}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-[#1E3A5F] hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 4 — CA card + invite
// ---------------------------------------------------------------------------
function CACard({ customerId }) {
  return (
    <Section testid="ca-primary-card" title="Your linked CA" subtitle="Read-only access · Phase 2 will add inline comments">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1E3A5F] text-[16px] font-bold text-white">PJ</div>
          <div>
            <div className="text-[14px] font-bold text-[#0F1B2D]">Pankaj Joshi & Associates</div>
            <div className="text-[12px] text-slate-500">Pune · Linked since Sep 2022 · Last login Apr 22</div>
            <div className="mt-1 flex items-center gap-3 text-[11.5px] text-slate-600">
              <span className="inline-flex items-center gap-1"><Phone size={11} /> +91 98XXXXX901</span>
              <span className="inline-flex items-center gap-1"><Mail size={11} /> ca@pjoshi.in</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a data-testid="ca-call-btn" href="tel:+9198XXXXX901" className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-[#1E3A5F] hover:bg-slate-50"><Phone size={11} /> Call</a>
          <a data-testid="ca-whatsapp-btn" href="https://wa.me/9198XXXXX901" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-emerald-700"><MessageCircle size={11} /> WhatsApp</a>
        </div>
      </div>

      <InvitePanel customerId={customerId} label="+ Invite another CA / consultant →" />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 5 — Fallback insights
// ---------------------------------------------------------------------------
const FALLBACK = [
  { id: "ai-1", severity: "red", title: "Resolve the ₹48,200 ITC mismatch by Apr 21",
    body: "Your GSTR-3B claim exceeds the GSTR-2B register by ₹48,200 across two suppliers. If unresolved before the 20 Apr filing it triggers an interest + penalty exposure of roughly ₹4,200/month.",
    action: "▶ Open the ITC Reconciliation doc in the queue and click Resolve. WhatsApps CA Pankaj Joshi automatically." },
  { id: "ai-2", severity: "amber", title: "Directors KYC filing window closes May 31",
    body: "MCA mandates DIN KYC before May 31 for both directors — penalty is ₹5,000 per DIN per year. CA needs Aadhaar + PAN + 2 photos uploaded to the portal.",
    action: "▶ Schedule a 30-min call with CA this week and pre-upload KYC docs to the Document Queue." },
  { id: "ai-3", severity: "green", title: "Auto-share of advance-tax saves 4 hrs / quarter",
    body: "Generating and emailing the FY27 Q1 advance-tax estimation directly from C-ORE saves manual entry and the back-and-forth WhatsApp follow-ups. CA already opened 18 reports in the last 12 months.",
    action: "▶ Click Generate on the Advance Tax row — share is one extra tap." },
  { id: "ai-4", severity: "blue", title: "Add a backup CA for filings season",
    body: "You have 1 CA linked. Adding a secondary CA / GST consultant means sick-leave or vacation never blocks a filing window, especially across the Sep-Dec compliance peak.",
    action: "▶ Use 'Invite another CA / consultant' below — they get read-only access, you stay in control." },
];

// ---------------------------------------------------------------------------
// SECTION 6 — Chat preload
// ---------------------------------------------------------------------------
const PRELOAD = [
  { role: "user", text: "What's the most urgent thing for my CA today?" },
  { role: "ai",   text: "The single most urgent item is the ₹48,200 ITC mismatch — it must be reconciled before the 20 Apr GSTR-3B filing or you risk roughly ₹4,200/month in interest plus a 10% penalty. Open the ITC Reconciliation row in the Document Queue and click Resolve — it sends an auto-WhatsApp to CA Pankaj Joshi at +91 98XXXXX901. Do this in the next 24 hours so the CA has 48 hrs to file before the deadline." },
];

function ActiveCAEmpty({ icon: Icon, title, copy }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
      {Icon && <Icon size={14} className="mt-0.5 shrink-0 text-slate-400" />}
      <div>
        <div className="font-semibold text-slate-700">{title}</div>
        <div>{copy}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function CAPortal() {
  const { customerData, dataAvailable, companyName, reload } = useCustomerData();

  // ----- Active customer branch: real CA portal state or dashes -----
  if (true) {
    const params = customerData?.params || {};
    const updatedAt = params.params_updated_at || {};
    const customerId = customerData?.customer_id
      || (() => { try { return getCurrentCustomer()?.id || null; } catch (_e) { return null; } })();
    const realKpis = [
      { id: "linked",   label: "CA Linked",          value: null, sub: "Invite your CA to enable real-time collaboration", status: "blue",  icon: UserPlus,
        tooltip: "Use 'Invite a CA' below to link one. They get read-only access via a signed link." },
      { id: "shared",   label: "Reports Shared",     value: null, sub: "Auto-shared once a CA is linked",                  status: "blue",  icon: Share2,
        tooltip: "Tracks how many PDF reports have been emailed/WhatsApped to your CA." },
      { id: "login",    label: "Last CA Login",      value: null, sub: "Available once a CA logs in",                      status: "blue",  icon: Calendar,
        tooltip: "Timestamp of the CA's last view of your read-only portal." },
      { id: "pending",  label: "Pending CA Actions", value: null, sub: "Items waiting on the CA",                          status: "blue",  icon: AlertTriangle,
        tooltip: "Documents waiting on the CA to view/comment." },
    ];

    return (
      <div data-testid="ca-module" className="space-y-5 px-6 py-5">
        <ModuleHeader
          overline="CA & Accountant Portal"
          title={companyName ? `${companyName} — collaborate with your CA in real time` : "Your CA, always in the loop"}
          right={<><RefreshCw size={13} className="text-emerald-600" />Auto-shared docs · WhatsApp pings</>}
        />

        <section data-testid="ca-kpis" className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {realKpis.map((k) => (
            <KpiCard key={k.id} kpi={k} testidPrefix="ca-kpi" dataAvailable={dataAvailable}
              realValue={null} realText={null} />
          ))}
        </section>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <Section
              testid="ca-doc-queue"
              title="Shared document queue"
              subtitle="Reports auto-shared with your CA once you link them"
            >
              <ActiveCAEmpty
                icon={FileText}
                title="No reports shared yet"
                copy="Link your CA from the card below. After that, monthly P&L, GST summaries, and the board pack get auto-shared on the 5th of every month."
              />
            </Section>
          </div>
          <div>
            <Section testid="ca-workflow" title="CA Workflow Queue" subtitle="What your CA needs from you next">
              <ActiveCAEmpty
                icon={Info}
                title="No pending CA actions"
                copy="Once a CA is linked, items like GST mismatches, KYC renewals and advance-tax estimates will appear here in priority order."
              />
            </Section>
          </div>
        </div>

        {/* Real CA-link card — uses dashes for any field that's not yet captured */}
        <Section testid="ca-primary-card" title="Your linked CA" subtitle="Read-only access · Phase 2 will add inline comments">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200 text-[16px] font-bold text-slate-500">—</div>
              <div>
                <div className="text-[14px] font-bold text-slate-400">No CA linked</div>
                <div className="text-[12px] text-slate-500">Invite your CA below to give them read-only access to your dashboard.</div>
              </div>
            </div>
          </div>
          <InvitePanel customerId={customerId} />
        </Section>

        {/* Compliance deadlines editor */}
        <Section
          testid="ca-compliance"
          title="Compliance deadlines"
          subtitle="GST filings, ITR, KYC renewals, advance tax — never miss one"
        >
          <ComplianceEditor
            customerId={customerId}
            value={params.compliance_deadlines}
            lastUpdatedIso={updatedAt.compliance_deadlines}
            onSaved={reload}
            disabled={!customerId}
          />
        </Section>

        <InsightsGrid
          subtitle="What your CA Portal advisor would tell you today"
          fallback={[]}
          fetchFn={api.caportalInsights}
          testidPrefix="ca"
          dataAvailable={dataAvailable}
        />

        <ChatPanel
          title="Ask your CA Portal advisor"
          subtitle="Compliance, GST, filings · Hindi or English"
          placeholder="Ask about GST, KYC, advance tax, or CA collaboration…"
          preload={PRELOAD}
          sendFn={api.caportalChat}
          testidPrefix="ca"
          emptyHint="Ask anything CA-related. Try: 'Am I ready for GSTR-3B filing?' or 'How do I share my P&L with my CA?'"
        />
      </div>
    );
  }

  // ----- Visitor / demo branch — original hardcoded content -----
  // Even in demo mode, if a real customer is signed in (e.g. they pressed
  // "Explore demo" while logged in), we still want their CA invite to go
  // out from their actual workspace.
  const demoCustomerId = (() => {
    try { return getCurrentCustomer()?.id || null; } catch (_e) { return null; }
  })();
  return (
    <div data-testid="ca-module" className="space-y-5 px-6 py-5">
      <ModuleHeader
        overline="CA & Accountant Portal"
        title="Your CA, always in the loop"
        right={<><RefreshCw size={13} className="text-emerald-600" />Auto-shared docs · WhatsApp pings</>}
      />

      <section data-testid="ca-kpis" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {KPIS.map((k) => <KpiCard key={k.id} kpi={k} testidPrefix="ca-kpi" />)}
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2"><DocumentQueue /></div>
        <div><WorkflowQueue /></div>
      </div>

      <CACard customerId={demoCustomerId} />

      <InsightsGrid
        subtitle="What your CA Portal advisor would tell you today"
        fallback={FALLBACK}
        fetchFn={api.caportalInsights}
        testidPrefix="ca"
      />

      <ChatPanel
        title="Ask your CA Portal advisor"
        subtitle="Compliance, GST, filings · Hindi or English"
        placeholder="Ask about GST, KYC, advance tax, or CA collaboration…"
        preload={PRELOAD}
        sendFn={api.caportalChat}
        testidPrefix="ca"
      />
    </div>
  );
}

// Lightweight invite-CA form. Used in BOTH the demo CACard (linked-CA flow)
// and the active customer's "no linked CA yet" empty-state.
function InvitePanel({ customerId, label }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [caName, setCaName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [expiresHuman, setExpiresHuman] = useState(null);

  const reset = () => {
    setEmail(""); setCaName(""); setMessage("");
    setSent(false); setError(null); setSending(false); setExpiresHuman(null);
  };
  const close = () => { setOpen(false); reset(); };

  const sendInvite = async () => {
    if (!email.trim()) return;
    if (!customerId) {
      setError("Please sign up or log in first — invites are sent from your C-ORE workspace.");
      return;
    }
    setSending(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca/invite`, {
        method:  "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          ca_email:    email.trim(),
          ca_name:     caName.trim() || undefined,
          message:     message.trim() || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        const reason = (detail && typeof detail === "object") ? (detail.reason || detail.error) : detail;
        throw new Error(reason || `Failed (HTTP ${res.status})`);
      }
      setExpiresHuman(body.expires_human || null);
      setSent(true);
    } catch (e) {
      setError(e.message || "Send failed — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="ca-invite-btn"
        onClick={() => setOpen(true)}
        className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-3 text-[13px] font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
      >
        {label || "+ Invite a CA / consultant →"}
      </button>

      <Modal open={open} onClose={close} title="Invite CA / consultant" testid="ca-invite-modal">
        {sent ? (
          <div data-testid="ca-invite-sent" className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-3 text-center text-[12.5px] font-semibold text-emerald-700">
            <div>✓ Invite sent to <span className="font-bold">{email}</span></div>
            <div className="text-[11.5px] font-normal text-emerald-800/80">
              They'll receive a read-only access email
              {expiresHuman ? ` — link valid till ${expiresHuman}.` : "."}
            </div>
            <button onClick={close} className="mt-2 inline-flex items-center justify-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700">
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[12.5px] text-slate-700">
              Enter your CA's email. They'll get a read-only invite link that expires in 7 days.
            </div>
            <input
              data-testid="ca-invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ca@firmname.in"
              disabled={sending}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[12.5px] focus:border-blue-500 focus:outline-none"
            />
            <input
              data-testid="ca-invite-name"
              type="text"
              value={caName}
              onChange={(e) => setCaName(e.target.value)}
              placeholder="CA name (optional, helps personalise)"
              disabled={sending}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[12.5px] focus:border-blue-500 focus:outline-none"
            />
            <textarea
              data-testid="ca-invite-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional note for your CA (e.g. 'Please review GSTR-3B by 20th')"
              disabled={sending}
              rows={2}
              maxLength={400}
              className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-[12.5px] focus:border-blue-500 focus:outline-none"
            />
            {error && (
              <div data-testid="ca-invite-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
                {error}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                data-testid="ca-invite-send"
                onClick={sendInvite}
                disabled={sending || !email.trim()}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {sending
                  ? <><Loader2 size={12} className="animate-spin" /> Sending…</>
                  : <><Send size={12} /> Send invite</>}
              </button>
              <button
                data-testid="ca-invite-cancel"
                onClick={close}
                disabled={sending}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-[#1E3A5F] hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
